import * as THREE from 'three';
import type { Enemy } from './enemy';

const SEPARATION_RADIUS = 1.3;
const SEPARATION_STRENGTH = 0.6;
/** Two entities more than this far apart vertically are on a different band (a ground rat vs. a hovering pigeon) and shouldn't nudge each other apart even when their XZ happens to coincide. */
const SEPARATION_VERTICAL_BAND = 2;

/**
 * Cheap O(n^2) pairwise separation — moved out of ai/spawner.ts, which
 * previously carried this as an explicitly-labeled stand-in ("S03 owns the
 * real steering system"). Still O(n^2): fine at this scale, since §3's own
 * ≤24-active-AI budget keeps n small — a spatial hash would only pay for
 * itself at a population this game will never reach in one block.
 *
 * S08 memory audit: this ran unconditionally every tick (one fresh
 * `new THREE.Vector3()` per enemy, regardless of whether anyone was ever
 * close enough to need a push) — at §3's ≤24-enemy ceiling that's up to 1440
 * short-lived allocations/sec from this one call alone, exactly the kind of
 * sustained GC churn a mobile GPU/CPU feels as stutter during a crowded
 * fight. `scratch` is a caller-owned, ever-growing pool (never shrinks, so a
 * population spike never re-triggers growth on a later, smaller tick) —
 * every entry is zeroed in place and reused instead of reallocated. The
 * returned array is a VIEW (`.slice(0, enemies.length)` is deliberately
 * avoided — see below) into that pool, valid only until the next call, which
 * is fine here since every real call site (`EnemyManager.update()`) consumes
 * it fully within the same synchronous tick and never stores a reference
 * across ticks.
 */
export function computeSeparation(enemies: readonly Enemy[], scratch: THREE.Vector3[]): THREE.Vector3[] {
  while (scratch.length < enemies.length) scratch.push(new THREE.Vector3());
  for (let i = 0; i < enemies.length; i++) scratch[i].set(0, 0, 0);
  const separations = scratch; // same length semantics as the old `enemies.map(...)` result for every index < enemies.length; callers only ever index up to enemies.length - 1
  for (let i = 0; i < enemies.length; i++) {
    for (let j = i + 1; j < enemies.length; j++) {
      const a = enemies[i];
      const b = enemies[j];
      if (a.state === 'dead' || b.state === 'dead') continue;
      if (Math.abs(a.position.y - b.position.y) > SEPARATION_VERTICAL_BAND) continue;
      const dx = a.position.x - b.position.x;
      const dz = a.position.z - b.position.z;
      const d = Math.hypot(dx, dz);
      if (d > 0.001 && d < SEPARATION_RADIUS) {
        const push = ((SEPARATION_RADIUS - d) / SEPARATION_RADIUS) * SEPARATION_STRENGTH;
        separations[i].x += (dx / d) * push;
        separations[i].z += (dz / d) * push;
        separations[j].x -= (dx / d) * push;
        separations[j].z -= (dz / d) * push;
      }
    }
  }
  return separations;
}
